import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  marketplaceKPIBibleApi,
  type AIConsumptionRule,
  type AttributionKPI,
  type BenchmarkType,
  type ExecutiveDashboard,
  type ExperimentKPIField,
  type HealthScoreMethodology,
  type KPIBibleDashboard,
  type KPIBibleStatus,
  type KPICategory,
  type KPIDefinition,
  type KPIPhilosophyField,
  type KPIValidationResult,
} from '../../api/marketplace-kpi-bible';

type Tab = 'overview' | 'dictionary' | 'philosophy' | 'health' | 'experiments' | 'executive' | 'ai' | 'status';
type Theme = 'light' | 'dark';

export function MarketplaceKPIBiblePage() {
  const [theme, setTheme] = useState<Theme>('light');
  const dark = theme === 'dark';
  const [tab, setTab] = useState<Tab>('overview');
  const [dashboard, setDashboard] = useState<KPIBibleDashboard | null>(null);
  const [categories, setCategories] = useState<KPICategory[]>([]);
  const [kpis, setKpis] = useState<KPIDefinition[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedKPI, setSelectedKPI] = useState<KPIDefinition | null>(null);
  const [philosophy, setPhilosophy] = useState<KPIPhilosophyField[]>([]);
  const [health, setHealth] = useState<HealthScoreMethodology[]>([]);
  const [experimentFields, setExperimentFields] = useState<ExperimentKPIField[]>([]);
  const [attribution, setAttribution] = useState<AttributionKPI[]>([]);
  const [executive, setExecutive] = useState<ExecutiveDashboard[]>([]);
  const [benchmarks, setBenchmarks] = useState<BenchmarkType[]>([]);
  const [aiRules, setAiRules] = useState<AIConsumptionRule[]>([]);
  const [status, setStatus] = useState<KPIBibleStatus | null>(null);
  const [validateKey, setValidateKey] = useState('revenue');
  const [validateResult, setValidateResult] = useState<KPIValidationResult | null>(null);
  const [actionMsg, setActionMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [dash, cats, dict, phil, hlth, exp, attr, exec, bench, rules, st] = await Promise.all([
        marketplaceKPIBibleApi.dashboard(),
        marketplaceKPIBibleApi.categories(),
        marketplaceKPIBibleApi.dictionary(selectedCategory || undefined),
        marketplaceKPIBibleApi.philosophy(),
        marketplaceKPIBibleApi.healthMethodology(),
        marketplaceKPIBibleApi.experimentKPIs(),
        marketplaceKPIBibleApi.attributionKPIs(),
        marketplaceKPIBibleApi.executiveDashboards(),
        marketplaceKPIBibleApi.benchmarks(),
        marketplaceKPIBibleApi.aiConsumptionRules(),
        marketplaceKPIBibleApi.status(),
      ]);
      setDashboard(dash);
      setCategories(cats);
      setKpis(dict);
      setPhilosophy(phil);
      setHealth(hlth);
      setExperimentFields(exp);
      setAttribution(attr);
      setExecutive(exec);
      setBenchmarks(bench);
      setAiRules(rules);
      setStatus(st);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load KPI bible');
    }
  }, [selectedCategory]);

  useEffect(() => { refresh(); }, [refresh]);

  const loadKPI = async (key: string) => {
    try {
      const kpi = await marketplaceKPIBibleApi.getKPI(key);
      setSelectedKPI(kpi);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load KPI');
    }
  };

  const validateKPI = async () => {
    setBusy(true);
    setActionMsg(null);
    try {
      const result = await marketplaceKPIBibleApi.validate(validateKey);
      setValidateResult(result);
      setActionMsg(result.valid ? `KPI "${result.kpi_key}" is registered in MIB-000002` : `KPI "${result.kpi_key}" is NOT registered`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Validation failed');
    } finally {
      setBusy(false);
    }
  };

  const card = dark ? 'border-slate-800 bg-slate-900' : 'border-slate-200 bg-white';
  const muted = dark ? 'text-slate-400' : 'text-slate-500';
  const shell = dark ? 'bg-slate-950 text-slate-100' : 'bg-white text-slate-900';

  const tabs: { id: Tab; label: string }[] = [
    { id: 'overview', label: 'Executive Summary' },
    { id: 'dictionary', label: 'KPI Dictionary' },
    { id: 'philosophy', label: 'KPI Philosophy' },
    { id: 'health', label: 'Health Methodology' },
    { id: 'experiments', label: 'Experiment & Attribution' },
    { id: 'executive', label: 'Executive & Benchmarks' },
    { id: 'ai', label: 'AI Consumption Rules' },
    { id: 'status', label: 'Bible Status' },
  ];

  return (
    <div className={`space-y-6 rounded-xl p-1 ${shell}`}>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-teal-500">MIB-000002</p>
          <h1 className="text-2xl font-semibold">Marketplace KPI & Metrics Bible</h1>
          <p className={`text-sm ${muted}`}>
            Universal marketplace metrics, business formula dictionary, AI measurement framework and executive success indicators
          </p>
          <div className="mt-2 flex flex-wrap gap-2 text-xs">
            <Link to="/platform/marketplace-intelligence-bible" className="text-teal-500 hover:underline">MIB-000001 Intelligence Bible</Link>
            <span className={muted}>·</span>
            <Link to="/amazon/market-intel" className="text-teal-500 hover:underline">Market Intelligence</Link>
            <span className={muted}>·</span>
            <Link to="/platform/semantic-governance" className="text-teal-500 hover:underline">Semantic Layer</Link>
          </div>
        </div>
        <button type="button" onClick={() => setTheme(dark ? 'light' : 'dark')} className={`rounded-lg border px-3 py-1.5 text-sm ${card}`}>
          {dark ? 'Light mode' : 'Dark mode'}
        </button>
      </div>

      {error && <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-300">{error}</div>}
      {actionMsg && <div className="rounded-lg border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-300">{actionMsg}</div>}

      <div className="flex flex-wrap gap-2 border-b border-slate-200 pb-2 dark:border-slate-800">
        {tabs.map((t) => (
          <button key={t.id} type="button" onClick={() => setTab(t.id)} className={`rounded-lg px-3 py-1.5 text-sm ${tab === t.id ? 'bg-teal-600 text-white' : `${card} ${muted}`}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'overview' && dashboard && (
        <div className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {[
              { label: 'Bible ID', value: dashboard.bible_id },
              { label: 'Total KPIs', value: dashboard.total_kpis },
              { label: 'Categories', value: dashboard.categories },
              { label: 'Health Scores', value: dashboard.health_scores },
              { label: 'Executive Views', value: dashboard.executive_views },
              { label: 'Semantic Mappings', value: dashboard.semantic_mappings },
              { label: 'Version', value: dashboard.version },
              { label: 'Status', value: 'Authoritative Active' },
            ].map((item) => (
              <div key={item.label} className={`rounded-xl border p-4 ${card}`}>
                <p className={`text-xs uppercase ${muted}`}>{item.label}</p>
                <p className="mt-1 text-2xl font-semibold">{item.value}</p>
              </div>
            ))}
          </div>
          <div className={`rounded-xl border p-4 ${card}`}>
            <h3 className="font-medium">Depends On</h3>
            <div className="mt-2 flex flex-wrap gap-2">
              {dashboard.depends_on.map((d) => (
                <span key={d} className="rounded-full bg-teal-100 px-3 py-1 text-xs font-mono dark:bg-teal-900">{d}</span>
              ))}
            </div>
          </div>
          <div className={`rounded-xl border p-4 ${card}`}>
            <h3 className="font-medium">Category Breakdown</h3>
            <div className="mt-3 grid gap-2 md:grid-cols-3 lg:grid-cols-4">
              {dashboard.category_breakdown.map((c) => (
                <div key={c.category} className="flex justify-between text-sm">
                  <span>{c.name}</span>
                  <span className={muted}>{c.count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === 'dictionary' && (
        <div className="space-y-4">
          <div className="flex flex-wrap gap-3">
            <select value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)} className={`rounded-lg border px-3 py-2 text-sm ${card}`}>
              <option value="">All categories</option>
              {categories.map((c) => (
                <option key={c.category} value={c.category}>{c.name} ({c.count})</option>
              ))}
            </select>
          </div>
          <div className="grid gap-4 lg:grid-cols-2">
            <div className="overflow-x-auto rounded-xl border">
              <table className={`min-w-full text-sm ${card}`}>
                <thead>
                  <tr className={`border-b ${dark ? 'border-slate-800' : 'border-slate-200'}`}>
                    <th className="px-4 py-2 text-left">KPI</th>
                    <th className="px-4 py-2 text-left">Category</th>
                    <th className="px-4 py-2 text-left">Unit</th>
                  </tr>
                </thead>
                <tbody>
                  {kpis.map((k) => (
                    <tr key={k.key} className={`cursor-pointer border-b hover:bg-teal-50 dark:hover:bg-teal-950 ${dark ? 'border-slate-800' : 'border-slate-100'}`} onClick={() => loadKPI(k.key)}>
                      <td className="px-4 py-2 font-medium">{k.name}</td>
                      <td className="px-4 py-2 text-xs">{k.category}</td>
                      <td className="px-4 py-2 text-xs">{k.unit || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {selectedKPI && (
              <div className={`rounded-xl border p-4 ${card}`}>
                <h3 className="text-lg font-semibold">{selectedKPI.name}</h3>
                <p className={`mt-1 font-mono text-xs text-teal-500`}>{selectedKPI.key}</p>
                <dl className={`mt-4 space-y-2 text-sm ${muted}`}>
                  <div><dt className="font-medium text-inherit">Definition</dt><dd>{selectedKPI.business_definition}</dd></div>
                  <div><dt className="font-medium text-inherit">Formula</dt><dd className="font-mono text-xs">{selectedKPI.formula}</dd></div>
                  <div><dt className="font-medium text-inherit">Data Sources</dt><dd>{selectedKPI.data_sources.join(', ')}</dd></div>
                  <div><dt className="font-medium text-inherit">Refresh</dt><dd>{selectedKPI.refresh_frequency}</dd></div>
                  <div><dt className="font-medium text-inherit">Owner</dt><dd>{selectedKPI.owner}</dd></div>
                  {selectedKPI.improvement_strategy && <div><dt className="font-medium text-inherit">Improvement Strategy</dt><dd>{selectedKPI.improvement_strategy}</dd></div>}
                </dl>
              </div>
            )}
          </div>
          <div className={`rounded-xl border p-4 ${card}`}>
            <h3 className="font-medium">Validate KPI Key</h3>
            <div className="mt-3 flex flex-wrap gap-3">
              <input value={validateKey} onChange={(e) => setValidateKey(e.target.value)} className={`rounded-lg border px-3 py-2 text-sm ${card}`} placeholder="kpi_key" />
              <button type="button" disabled={busy} onClick={validateKPI} className="rounded-lg bg-teal-600 px-4 py-2 text-sm text-white disabled:opacity-50">
                {busy ? 'Validating…' : 'Validate KPI'}
              </button>
            </div>
            {validateResult && (
              <p className={`mt-2 text-sm ${validateResult.valid ? 'text-emerald-500' : 'text-amber-500'}`}>
                {validateResult.valid ? 'Registered' : 'Not registered'} — {validateResult.gaps?.join(', ')}
              </p>
            )}
          </div>
        </div>
      )}

      {tab === 'philosophy' && (
        <div className="space-y-4">
          <p className={`text-sm ${muted}`}>Every KPI SHALL answer:</p>
          <div className="grid gap-3 md:grid-cols-2">
            {philosophy.map((f) => (
              <div key={f.key} className={`rounded-xl border p-4 ${card}`}>
                <span className="text-teal-500">{f.order}.</span> {f.name}
              </div>
            ))}
          </div>
          <p className={`text-sm ${muted}`}>A KPI SHALL Measure, Explain, Predict, Recommend, and Learn — not merely display information.</p>
        </div>
      )}

      {tab === 'health' && (
        <div className="grid gap-3 md:grid-cols-2">
          {health.map((h) => (
            <div key={h.key} className={`rounded-xl border p-4 ${card}`}>
              <div className="flex items-center justify-between">
                <h3 className="font-medium">{h.name}</h3>
                <span className="text-xs text-teal-500">weight {(h.weightage * 100).toFixed(0)}%</span>
              </div>
              <p className={`mt-1 font-mono text-xs ${muted}`}>{h.formula}</p>
              <p className="mt-2 text-sm">{h.business_interpretation}</p>
              {h.suggested_actions && h.suggested_actions.length > 0 && (
                <ul className={`mt-2 list-inside list-disc text-xs ${muted}`}>
                  {h.suggested_actions.map((a) => <li key={a}>{a}</li>)}
                </ul>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === 'experiments' && (
        <div className="grid gap-6 md:grid-cols-2">
          <div>
            <h2 className="mb-3 text-lg font-semibold">Experiment KPI Fields</h2>
            <div className="space-y-2">
              {experimentFields.map((f) => (
                <div key={f.key} className={`rounded-lg border px-4 py-2 text-sm ${card}`}>
                  <span className="font-medium">{f.name}</span>
                  {f.required && <span className="ml-2 text-xs text-teal-500">required</span>}
                  <p className={`text-xs ${muted}`}>{f.description}</p>
                </div>
              ))}
            </div>
          </div>
          <div>
            <h2 className="mb-3 text-lg font-semibold">Attribution KPIs</h2>
            <div className="space-y-2">
              {attribution.map((a) => (
                <div key={a.key} className={`flex justify-between rounded-lg border px-4 py-2 text-sm ${card}`}>
                  <span>{a.name}</span>
                  <span className={`text-xs ${muted}`}>{a.ai_method}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === 'executive' && (
        <div className="grid gap-6 md:grid-cols-2">
          <div>
            <h2 className="mb-3 text-lg font-semibold">Executive Dashboards</h2>
            <div className="space-y-3">
              {executive.map((d) => (
                <div key={d.key} className={`rounded-xl border p-4 ${card}`}>
                  <h3 className="font-medium">{d.name}</h3>
                  {d.description && <p className={`text-sm ${muted}`}>{d.description}</p>}
                  <div className="mt-2 flex flex-wrap gap-1">
                    {d.kpis.map((k) => (
                      <span key={k} className="rounded bg-teal-100 px-2 py-0.5 font-mono text-xs dark:bg-teal-900">{k}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div>
            <h2 className="mb-3 text-lg font-semibold">Benchmark Types</h2>
            <div className="space-y-2">
              {benchmarks.map((b) => (
                <div key={b.key} className={`rounded-lg border px-4 py-2 text-sm ${card}`}>
                  <span className="font-medium">{b.name}</span>
                  <p className={`text-xs ${muted}`}>{b.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === 'ai' && (
        <div className="grid gap-3 md:grid-cols-2">
          {aiRules.map((r) => (
            <div key={r.key} className={`rounded-xl border p-4 ${card}`}>
              <div className="flex items-center justify-between">
                <h3 className="font-medium">{r.name}</h3>
                {r.mandatory && <span className="text-xs text-teal-500">Mandatory</span>}
              </div>
              <p className={`mt-1 text-sm ${muted}`}>{r.description}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'status' && status && (
        <div className={`rounded-xl border p-6 ${card}`}>
          <div className="grid gap-4 md:grid-cols-2">
            {Object.entries(status).map(([key, value]) => (
              <div key={key}>
                <p className={`text-xs uppercase ${muted}`}>{key.replace(/_/g, ' ')}</p>
                <p className="mt-1 font-medium">{Array.isArray(value) ? (value as string[]).join(', ') : String(value)}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
